import { Upload, Typography, Tag } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  FileOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Button } from "@rumbledirect/ui";
import type { UploadFile } from "antd/es/upload/interface";

const { Text } = Typography;

export interface UploadedFileInfo {
  name: string;
  size: string;
}

interface DocumentChecklistItemProps {
  name: string;
  description: string;
  required: boolean;
  initialUploaded: boolean;
  uploadedFile: UploadedFileInfo | null;
  onUpload: (file: UploadedFileInfo) => void;
  onRemove: () => void;
}

export const DocumentChecklistItem = ({
  name,
  description,
  required,
  initialUploaded,
  uploadedFile,
  onUpload,
  onRemove,
}: DocumentChecklistItemProps) => {
  const handleUpload = (file: UploadFile) => {
    const originalFile = file as unknown as File;
    const sizeInMB = (originalFile.size / (1024 * 1024)).toFixed(2);
    onUpload({ name: originalFile.name, size: `${sizeInMB} MB` });
    return false;
  };

  const uploaded = initialUploaded || uploadedFile !== null;

  return (
    <div className="border rounded-lg p-4 mb-3 transition-all hover:border-gray-400">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {uploaded ? (
              <CheckCircleOutlined className="text-green-500 text-base" />
            ) : required ? (
              <ExclamationCircleOutlined className="text-red-500 text-base" />
            ) : (
              <FileOutlined className="text-gray-400 text-base" />
            )}
            <Text strong>{name}</Text>
            {uploaded ? (
              <Tag color="green">Aangeleverd</Tag>
            ) : required ? (
              <Tag color="red">Ontbreekt</Tag>
            ) : (
              <Tag color="default">Optioneel</Tag>
            )}
          </div>
          <Text type="secondary" className="text-sm block ml-6">
            {description}
          </Text>

          {uploadedFile && (
            <div className="ml-6 mt-2 flex items-center gap-3 bg-gray-50 rounded px-3 py-2">
              <FileOutlined className="text-gray-500" />
              <div className="flex-1 min-w-0">
                <Text className="text-sm block truncate">
                  {uploadedFile.name}
                </Text>
                <Text type="secondary" className="text-xs">
                  {uploadedFile.size}
                </Text>
              </div>
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={onRemove}
              />
            </div>
          )}

          {initialUploaded && !uploadedFile && (
            <div className="ml-6 mt-2 flex items-center gap-3 bg-gray-50 rounded px-3 py-2">
              <CheckCircleOutlined className="text-green-500" />
              <Text type="secondary" className="text-sm">
                Eerder aangeleverd via bestuurder
              </Text>
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          <Upload
            showUploadList={false}
            beforeUpload={handleUpload}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          >
            <Button icon={<UploadOutlined />} size="small">
              {uploaded ? "Vervangen" : "Uploaden"}
            </Button>
          </Upload>
        </div>
      </div>
    </div>
  );
};
